import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AppConfig } from 'src/app/app-config.loader';
import { DocumentiSearchDocRow } from 'src/app/shared/models/documento-search.model';
import { DestroyReportFileFormat, DistruzioneBulkResponse, OperazioneMassivaDeclassificaRequest, OperazioneMassivaDistruzioneRequest, OperazioneMassivaResponse, OperazioneMassivaTipo, OperazioneMassivaVersamentoRequest } from '../models/operazioni-massive-service.model';
import { HeaderDeclassificaOperazioniMassive, HeaderDistruzioneOperazioniMassive, HeaderVersamentoOperazioniMassive, SummaryDocumentoToDestroy } from '../models/operazioni-massive.model';


@Injectable({
  providedIn: 'root'
})
export class OperazioniMassiveService {

  private readonly apiBaseUrl: string;

  constructor(private readonly appConfig: AppConfig, private readonly http: HttpClient) {
    this.apiBaseUrl = `${appConfig.settings.apiRoot}`;
  }

  // public protocollo(request: RegistriRequest): Observable<RegistroProtocollo[]> {
  //   return this.http.post<RegistroProtocollo[]>(`${this.apiBaseUrl}/registri/protocollo`, request);
  // }

  public getByOperationId(operationId: number): Observable<OperazioneMassivaResponse> {
    return this.http.get<OperazioneMassivaResponse>(`${this.apiBaseUrl}/operazionimassive/${operationId}`);
  }

  public getAll(type: OperazioneMassivaTipo[] | undefined | null, from: Date | null, to: Date | null, registerId: number | null, verbalId: string | null): Observable<OperazioneMassivaResponse[]> {
    let params = new HttpParams();

    if (type) {
      type.forEach(e => {
        params = params.append('type', e);
      })
    }
    if (from) {
      params = params.append('from', from.toISOString());
    }
    if (to) {
      params = params.append('to', to.toISOString());
    }
    if (registerId) {
      params = params.append('registerId', registerId);
    }
    if (verbalId) {
      params = params.append('verbalId', verbalId);
    }

    return this.http.get<OperazioneMassivaResponse[]>(`${this.apiBaseUrl}/operazionimassive`, { params: params });
  }

  public distruzione(header: HeaderDistruzioneOperazioniMassive, documents: SummaryDocumentoToDestroy[]): Observable<DistruzioneBulkResponse | undefined> {
    const request = this.ensureDistruzioneMassivaRequest(header, documents);

    return this.http.post<DistruzioneBulkResponse | undefined>(`${this.apiBaseUrl}/documenti/massive/distruggi`, request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.log("ERROR", error);
          return (of(undefined));
        })
      );
  }

  public declassifica(header: HeaderDeclassificaOperazioniMassive, documents: DocumentiSearchDocRow[]): Observable<boolean | undefined> {
    const request = this.ensureDeclassificaMassivaRequest(header, documents);

    return this.http.post<void>(`${this.apiBaseUrl}/documenti/massive/declassifica`, request)
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          console.log("ERROR", error);
          return (of(undefined));
        })
      );
  }

  public versamento(header: HeaderVersamentoOperazioniMassive, documents: DocumentiSearchDocRow[]): Observable<boolean | undefined> {
    const request = this.ensureVersamentoMassivaRequest(header, documents);

    return this.http.post<void>(`${this.apiBaseUrl}/documenti/massive/archivioDiStato`, request)
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          console.log("ERROR", error);
          return (of(undefined));
        })
      );
  }

  public verbaleDistruzione(operazioneId:number, format: DestroyReportFileFormat = DestroyReportFileFormat.PDF ): void {
    
    const params = new HttpParams().set('format',format);
    const contentType = this.getContentType(format);

    this.http.post<Blob>(`${this.apiBaseUrl}/operazionimassive/verbale/distruzione/${operazioneId}`,
                          null,
                          { params:params, responseType: 'blob' as 'json', observe: 'response'}
                        )
      .subscribe(res => {
        if (!res.body) return;

        const xFileName = res.headers.get('X-Filename');
        const fileName = this.ensureFileName(xFileName, format);

        this.prepareForDownload(fileName, contentType, res.body);
      });
  }

  public report(operazioneId:number, format: DestroyReportFileFormat = DestroyReportFileFormat.PDF ): void {
    
    const params = new HttpParams().set('format',format);
    const contentType = this.getContentType(format);

    this.http.post<Blob>(`${this.apiBaseUrl}/operazionimassive/report/${operazioneId}`,
                          null,
                          { params:params, responseType: 'blob' as 'json', observe: 'response'}
                        )
      .subscribe(res => {
        if (!res.body) return;

        const xFileName = res.headers.get('X-Filename');
        const fileName = this.ensureFileName(xFileName, format);

        this.prepareForDownload(fileName, contentType, res.body);
      });
  }

  private prepareForDownload(fileName: string, contentType: string, body: Blob | null): void {
    if (!body) return;

    const file = new File([body], fileName, { type: contentType })        
    const url = window.URL.createObjectURL(file);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(url);
  }

  private getContentType( format: DestroyReportFileFormat = DestroyReportFileFormat.PDF) : string {
    let contentType = '';

    switch (format) {
      case  DestroyReportFileFormat.PDF :     
      contentType = 'application/pdf';
      break;
      case  DestroyReportFileFormat.Word :     
      contentType =  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      break;      
    }

    return contentType;
  }

  private   ensureFileName(fileName: string|null, format: DestroyReportFileFormat = DestroyReportFileFormat.PDF) : string {
    let result = 'report_di_riepilogo' ;

    switch (format) {
      case  DestroyReportFileFormat.PDF :     
      result += '.pdf';
      break;
      case  DestroyReportFileFormat.Word :     
      result += '.docx';
      break;      
    }

    return fileName??result;
  } 

  private ensureDistruzioneMassivaRequest(header: HeaderDistruzioneOperazioniMassive, documents: SummaryDocumentoToDestroy[]): OperazioneMassivaDistruzioneRequest {

    const dataDistruzione = (header.dataDistruzione)?header.dataDistruzione.toISOString():null;

    const result: OperazioneMassivaDistruzioneRequest = {
      attributi: { data: dataDistruzione, mezzo: header.mezzoDistruzione, numeroNota: header.ordine, salvaSuRegistro: header.salvaSuRegistro },
      dati: JSON.stringify({ header, documents }),
      tipo: OperazioneMassivaTipo.Distruzione,
      dataOperazione: new Date(Date.now()),
      numDocumenti: documents.length,
      numeroVerbale: header.ordine,
      documentoIds: documents.map(d => d.id)
    }

    return result;
  }

  private ensureDeclassificaMassivaRequest(header: HeaderDeclassificaOperazioniMassive, documents: DocumentiSearchDocRow[]): OperazioneMassivaDeclassificaRequest {
    const result: OperazioneMassivaDeclassificaRequest = {
      attributi: { dateFrom: header.dataDeclassifica, classification: header.classifica, note: header.nota??'', vietataDivulgazione: header.vietataDivulgazione },
      dati: JSON.stringify({ header, documents }),
      tipo: OperazioneMassivaTipo.Declassifica,
      dataOperazione: new Date(Date.now()),
      numDocumenti: documents.length,
      numeroVerbale: '',
      documentoIds: documents.map(d => d.documentoId)
    }

    return result;
  }

  private ensureVersamentoMassivaRequest(header: HeaderVersamentoOperazioniMassive, documents: DocumentiSearchDocRow[]): OperazioneMassivaVersamentoRequest {
    const result: OperazioneMassivaVersamentoRequest = {
      attributi: { date: header.date, numeroNota: header.numeroNota, note: header.note },
      dati: JSON.stringify({ header, documents }),
      tipo: OperazioneMassivaTipo.Versamento,
      dataOperazione: new Date(Date.now()),
      numDocumenti: documents.length,
      numeroVerbale: '',
      documentoIds: documents.map(d => d.documentoId)
    }

    return result;
  }
}

